// src/pages/About.jsx

import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";

import {
  FaArrowRight,
  FaCheck,
  FaCode,
  FaMobileAlt,
  FaBullhorn,
  FaUsers,
  FaRocket,
  FaLaptopCode,
  FaChartLine,
  FaGlobe,
  FaHandshake,
  FaLightbulb,
  FaShieldAlt,
  FaQuoteLeft,
  FaStar,
} from "react-icons/fa";

import founderImg from "../assets/founder.jpeg";

const stats = [
  {
    value: "120+",
    label: "Projects Delivered",
    icon: <FaRocket />,
  },
  {
    value: "30+",
    label: "Industries Served",
    icon: <FaGlobe />,
  },
  {
    value: "100%",
    label: "Client Focus",
    icon: <FaHandshake />,
  },
  {
    value: "24/7",
    label: "Support Available",
    icon: <FaShieldAlt />,
  },
];

const coreServices = [
  {
    icon: <FaCode />,
    number: "01",
    title: "Web Development",
    description:
      "Professional websites and custom web applications designed around your business goals.",
  },
  {
    icon: <FaMobileAlt />,
    number: "02",
    title: "Mobile App Development",
    description:
      "Business, e-commerce, booking, healthcare and custom mobile applications.",
  },
  {
    icon: <FaBullhorn />,
    number: "03",
    title: "Digital Marketing",
    description:
      "Social media, SEO, paid advertising and lead generation focused on business growth.",
  },
  {
    icon: <FaUsers />,
    number: "04",
    title: "CRM Development",
    description:
      "Custom CRM and business management systems to organize your operations.",
  },
];

const strengths = [
  {
    icon: <FaRocket />,
    title: "Fast & Practical",
    description:
      "We focus on useful solutions and efficient development without unnecessary complexity.",
  },
  {
    icon: <FaLaptopCode />,
    title: "Modern Technology",
    description:
      "We use modern technologies to create responsive, scalable and reliable digital products.",
  },
  {
    icon: <FaUsers />,
    title: "Client Focused",
    description:
      "Every project starts by understanding the actual requirements of your business.",
  },
  {
    icon: <FaChartLine />,
    title: "Growth Oriented",
    description:
      "Our solutions are designed to support visibility, customer engagement and business growth.",
  },
];

const industries = [
  "Healthcare",
  "Education",
  "E-Commerce",
  "Real Estate",
  "Restaurants",
  "Hospitality",
  "Retail",
  "Professional Services",
];

export default function About() {
  useEffect(() => {
    AOS.init({
      duration: 750,
      once: true,
      easing: "ease-out-cubic",
      offset: 70,
    });

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  return (
    <main className="relative min-h-screen overflow-hidden bg-[#f8fbff] px-5 pb-24 pt-28 font-[Manrope] text-slate-900 sm:px-8 lg:px-12">
      {/* =====================================================
          BACKGROUND
      ====================================================== */}

      <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        {/* Grid */}
        <div
          className="absolute inset-0 opacity-60"
          style={{
            backgroundImage:
              "linear-gradient(rgba(37,99,235,.045) 1px, transparent 1px), linear-gradient(90deg, rgba(37,99,235,.045) 1px, transparent 1px)",
            backgroundSize: "70px 70px",
            maskImage:
              "linear-gradient(to bottom, black 0%, black 45%, transparent 85%)",
          }}
        />

        {/* Blue glow */}
        <div className="absolute -left-48 top-0 h-[420px] w-[420px] animate-pulse rounded-full bg-blue-200/30 blur-[110px]" />

        <div className="absolute -right-48 top-[30%] h-[460px] w-[460px] rounded-full bg-cyan-200/25 blur-[120px]" />

        <div className="absolute bottom-[-180px] left-[35%] h-[450px] w-[450px] rounded-full bg-indigo-200/20 blur-[120px]" />

        {/* Floating dots */}
        <span className="absolute left-[8%] top-[15%] h-2 w-2 animate-pulse rounded-full bg-blue-400/60" />

        <span className="absolute right-[14%] top-[30%] h-1.5 w-1.5 animate-ping rounded-full bg-cyan-400/60" />

        <span className="absolute bottom-[25%] left-[20%] h-1.5 w-1.5 animate-pulse rounded-full bg-indigo-400/50" />
      </div>

      <div className="mx-auto max-w-7xl">
        {/* =====================================================
            HERO
        ====================================================== */}

        <section
          className="relative mx-auto max-w-4xl text-center"
          data-aos="fade-up"
        >
          <div className="inline-flex animate-pulse items-center gap-2 rounded-full border border-blue-200 bg-white/90 px-4 py-2 text-[10px] font-extrabold tracking-[0.22em] text-blue-600 shadow-sm backdrop-blur">
            <span className="h-1.5 w-1.5 rounded-full bg-blue-600" />
            ABOUT PRIMEAPPCONNECT
          </div>

          <h1 className="mt-6 text-4xl font-extrabold leading-[1.08] tracking-[-0.045em] text-slate-950 sm:text-5xl lg:text-[3.8rem]">
            We Build Digital
            <span className="mt-1 block bg-gradient-to-r from-blue-600 via-sky-500 to-indigo-600 bg-clip-text text-transparent">
              Solutions That Grow
            </span>
          </h1>

          <p className="mx-auto mt-6 max-w-2xl text-sm leading-7 text-slate-500 sm:text-base">
            PrimeAppConnect helps businesses turn ideas into powerful digital
            products — from websites and mobile apps to marketing solutions
            and custom business systems.
          </p>

          <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
            <Link
              to="/contact"
              className="group inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-6 py-3.5 text-sm font-extrabold text-white shadow-[0_14px_35px_rgba(37,99,235,.18)] transition-all duration-300 hover:-translate-y-1 hover:bg-blue-700 hover:shadow-[0_20px_45px_rgba(37,99,235,.25)]"
            >
              Talk to Our Team
              <FaArrowRight className="text-xs transition-transform duration-300 group-hover:translate-x-1" />
            </Link>

            <Link
              to="/services"
              className="group inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-6 py-3.5 text-sm font-extrabold text-slate-700 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-blue-200 hover:text-blue-600 hover:shadow-md"
            >
              Explore Our Services

              <FaArrowRight className="text-xs opacity-0 transition-all duration-300 group-hover:translate-x-1 group-hover:opacity-100" />
            </Link>
          </div>
        </section>

        {/* =====================================================
            WHO WE ARE
        ====================================================== */}

        <section className="mt-20 grid items-center gap-12 lg:grid-cols-2">
          {/* Image */}

          <div data-aos="fade-right" className="group relative">
            <div className="absolute -inset-4 rounded-[2rem] bg-blue-400/10 blur-3xl transition duration-700 group-hover:bg-blue-400/20" />

            <div className="relative overflow-hidden rounded-[1.75rem] border border-slate-200 bg-white p-2 shadow-[0_25px_70px_rgba(15,23,42,.10)] transition duration-500 group-hover:-translate-y-1 group-hover:shadow-[0_35px_90px_rgba(37,99,235,.13)]">
              <div className="relative aspect-[4/3] overflow-hidden rounded-[1.35rem]">
                <img
                  src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=1400&q=85"
                  alt="PrimeAppConnect team working together"
                  className="h-full w-full object-cover transition duration-700 ease-out group-hover:scale-105"
                />

                <div className="absolute inset-0 bg-gradient-to-t from-blue-950/60 via-transparent to-transparent" />

                <div className="absolute bottom-5 left-5">
                  <div className="flex items-center gap-2 rounded-full border border-white/20 bg-white/15 px-4 py-2 text-xs font-bold text-white shadow-lg backdrop-blur-md">
                    <span className="h-2 w-2 animate-pulse rounded-full bg-cyan-300" />
                    Building Digital Experiences
                  </div>
                </div>
              </div>
            </div>

            {/* Floating card */}

            <div className="absolute -bottom-5 -right-3 hidden animate-bounce rounded-2xl border border-blue-100 bg-white px-5 py-4 shadow-[0_20px_50px_rgba(15,23,42,.12)] sm:block">
              <p className="text-[10px] font-extrabold uppercase tracking-[0.18em] text-slate-400">
                Our Approach
              </p>

              <p className="mt-1 text-sm font-extrabold text-slate-800">
                Simple. Practical. Scalable.
              </p>
            </div>
          </div>

          {/* Content */}

          <div data-aos="fade-left">
            <span className="text-[10px] font-extrabold tracking-[0.22em] text-blue-600">
              WHO WE ARE
            </span>

            <h2 className="mt-4 text-3xl font-extrabold leading-tight tracking-[-0.035em] text-slate-950 sm:text-4xl">
              Your Digital
              <span className="block bg-gradient-to-r from-blue-600 to-sky-500 bg-clip-text text-transparent">
                Growth Partner
              </span>
            </h2>

            <p className="mt-6 text-sm leading-7 text-slate-500 sm:text-base">
              PrimeAppConnect is a digital solutions company focused on
              helping businesses establish and strengthen their digital
              presence.
            </p>

            <p className="mt-4 text-sm leading-7 text-slate-500 sm:text-base">
              We combine development, design, technology and marketing to
              create practical digital solutions built around the unique needs
              of every business.
            </p>

            <div className="mt-7 grid gap-3 sm:grid-cols-2">
              {[
                "Business-focused solutions",
                "Modern responsive design",
                "Scalable technology",
                "Long-term support",
              ].map((item, index) => (
                <div
                  key={item}
                  data-aos="fade-up"
                  data-aos-delay={index * 70}
                  className="group flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-600 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-blue-200 hover:shadow-md"
                >
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-50 text-[10px] text-blue-600 transition duration-300 group-hover:scale-110 group-hover:bg-blue-600 group-hover:text-white">
                    <FaCheck />
                  </span>

                  {item}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* =====================================================
            FOUNDER
        ====================================================== */}

        <section
          className="mt-24 overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-[0_25px_80px_rgba(15,23,42,.08)]"
          data-aos="fade-up"
        >
          <div className="grid lg:grid-cols-[0.78fr_1.22fr]">
            {/* Founder Image */}

            <div className="group relative min-h-[430px] overflow-hidden">
              <img
                src={founderImg}
                alt="Founder of PrimeAppConnect"
                className="absolute inset-0 h-full w-full object-cover object-center transition duration-700 group-hover:scale-105"
              />

              <div className="absolute inset-0 bg-gradient-to-t from-blue-950/75 via-blue-950/10 to-transparent lg:bg-gradient-to-r lg:from-transparent lg:to-white/10" />

              <div className="absolute bottom-6 left-6">
                <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/15 px-4 py-2 text-xs font-bold text-white backdrop-blur-md">
                  <span className="h-2 w-2 rounded-full bg-cyan-300" />
                  Founder
                </span>
              </div>
            </div>

            {/* Founder Content */}

            <div className="relative flex flex-col justify-center p-7 sm:p-10 lg:p-14">
              <span className="text-[10px] font-extrabold tracking-[0.22em] text-blue-600">
                LEADERSHIP
              </span>

              <h2 className="mt-4 text-3xl font-extrabold tracking-tight text-slate-950 sm:text-4xl">
                Sai Kumar
              </h2>

              <p className="mt-2 text-sm font-extrabold text-blue-600">
                Founder — PrimeAppConnect
              </p>

              <div className="mt-6 h-1 w-12 rounded-full bg-blue-600" />

              <div className="relative mt-7">
                <FaQuoteLeft className="absolute -left-1 -top-2 text-xl text-blue-100" />

                <p className="relative max-w-2xl pl-5 text-sm leading-7 text-slate-500 sm:text-base">
                  PrimeAppConnect was founded with the vision of making modern
                  digital solutions accessible to businesses that want to
                  build, improve and grow their online presence.
                </p>
              </div>

              <p className="mt-4 max-w-2xl text-sm leading-7 text-slate-500 sm:text-base">
                With a focus on understanding the business first, we aim to
                create technology that is practical, scalable and genuinely
                useful for our clients.
              </p>

              <div className="mt-7 flex flex-wrap gap-3">
                {[
                  "Digital Solutions",
                  "Business Technology",
                  "Growth Focused",
                ].map((item) => (
                  <span
                    key={item}
                    className="rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-xs font-bold text-slate-500 transition duration-300 hover:-translate-y-0.5 hover:border-blue-200 hover:bg-blue-50 hover:text-blue-600"
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* =====================================================
            STATS
        ====================================================== */}

        <section className="mt-20" data-aos="fade-up">
          <div className="grid grid-cols-2 overflow-hidden rounded-[1.75rem] border border-slate-200 bg-white shadow-[0_15px_50px_rgba(15,23,42,.06)] sm:grid-cols-4">
            {stats.map((stat, index) => (
              <div
                key={stat.label}
                className={`group relative p-6 text-center transition duration-300 hover:bg-blue-50/50 sm:p-8 ${
                  index % 2 !== 0 ? "border-l border-slate-200" : ""
                } ${
                  index > 1
                    ? "border-t border-slate-200 sm:border-t-0"
                    : ""
                } ${
                  index === 2 ? "sm:border-l border-slate-200" : ""
                }`}
              >
                <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-sm text-blue-600 transition duration-300 group-hover:-translate-y-1 group-hover:scale-110 group-hover:bg-blue-600 group-hover:text-white">
                  {stat.icon}
                </div>

                <p className="mt-4 text-2xl font-extrabold tracking-tight text-slate-950 sm:text-3xl">
                  {stat.value}
                </p>

                <p className="mt-2 text-xs font-semibold text-slate-400 sm:text-sm">
                  {stat.label}
                </p>

                <div className="absolute bottom-0 left-1/2 h-0.5 w-0 -translate-x-1/2 bg-blue-600 transition-all duration-500 group-hover:w-1/2" />
              </div>
            ))}
          </div>
        </section>

        {/* =====================================================
            SERVICES
        ====================================================== */}

        <section className="mt-24" data-aos="fade-up">
          <div className="mx-auto max-w-2xl text-center">
            <span className="text-[10px] font-extrabold tracking-[0.22em] text-blue-600">
              WHAT WE DO
            </span>

            <h2 className="mt-4 text-3xl font-extrabold tracking-[-0.035em] text-slate-950 sm:text-4xl">
              Digital Services
              <span className="block bg-gradient-to-r from-blue-600 to-sky-500 bg-clip-text text-transparent">
                Under One Roof
              </span>
            </h2>

            <p className="mt-4 text-sm leading-7 text-slate-500">
              From your first website to a complete digital business system,
              we provide solutions designed around your goals.
            </p>
          </div>

          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {coreServices.map((service, index) => (
              <div
                key={service.title}
                data-aos="fade-up"
                data-aos-delay={index * 80}
                className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-all duration-500 hover:-translate-y-2 hover:border-blue-200 hover:shadow-[0_20px_50px_rgba(37,99,235,.10)]"
              >
                {/* Top line */}

                <div className="absolute left-0 right-0 top-0 h-0.5 origin-left scale-x-0 bg-blue-600 transition duration-500 group-hover:scale-x-100" />

                <div className="flex items-center justify-between">
                  <span className="text-xs font-extrabold text-slate-300">
                    {service.number}
                  </span>

                  <FaArrowRight className="text-xs text-slate-300 transition duration-300 group-hover:-rotate-45 group-hover:text-blue-600" />
                </div>

                <div className="mt-9 flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 text-xl text-blue-600 transition duration-500 group-hover:rotate-3 group-hover:scale-110 group-hover:bg-blue-600 group-hover:text-white">
                  {service.icon}
                </div>

                <h3 className="mt-6 text-lg font-extrabold tracking-tight text-slate-900">
                  {service.title}
                </h3>

                <p className="mt-3 text-sm leading-7 text-slate-500">
                  {service.description}
                </p>

                <div className="mt-5 h-0.5 w-8 bg-blue-600 transition-all duration-500 group-hover:w-16" />
              </div>
            ))}
          </div>
        </section>

        {/* =====================================================
            MISSION + APPROACH
        ====================================================== */}

        <section className="mt-24 grid gap-6 lg:grid-cols-2">
          {/* Mission */}

          <div
            data-aos="fade-right"
            className="group relative overflow-hidden rounded-[1.75rem] border border-blue-100 bg-blue-50/60 p-7 transition-all duration-500 hover:-translate-y-1 hover:shadow-xl hover:shadow-blue-100 sm:p-9"
          >
            <div className="absolute -right-16 -top-16 h-40 w-40 rounded-full bg-blue-200/30 blur-3xl transition duration-500 group-hover:scale-125 group-hover:bg-blue-200/50" />

            <div className="relative">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white text-blue-600 shadow-sm transition duration-300 group-hover:scale-110">
                <FaLightbulb />
              </div>

              <span className="mt-6 block text-[10px] font-extrabold tracking-[0.22em] text-blue-600">
                OUR MISSION
              </span>

              <h2 className="mt-3 text-2xl font-extrabold tracking-tight text-slate-950 sm:text-3xl">
                Technology That Solves Real Problems
              </h2>

              <p className="mt-4 text-sm leading-7 text-slate-500">
                Our mission is to help businesses use technology effectively —
                not simply to have a website or application, but to create
                digital systems that genuinely support their business goals.
              </p>

              <div className="mt-6 flex items-center gap-3 text-sm font-bold text-slate-700">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white text-blue-600 shadow-sm">
                  <FaCheck />
                </span>
                Practical digital solutions
              </div>
            </div>
          </div>

          {/* Approach */}

          <div
            data-aos="fade-left"
            className="group relative overflow-hidden rounded-[1.75rem] border border-slate-200 bg-white p-7 shadow-sm transition-all duration-500 hover:-translate-y-1 hover:shadow-xl sm:p-9"
          >
            <div className="absolute -right-16 -top-16 h-40 w-40 rounded-full bg-blue-100/70 blur-3xl transition duration-500 group-hover:scale-125" />

            <div className="relative">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50 text-blue-600 transition duration-300 group-hover:scale-110">
                <FaRocket />
              </div>

              <span className="mt-6 block text-[10px] font-extrabold tracking-[0.22em] text-blue-600">
                OUR APPROACH
              </span>

              <h2 className="mt-3 text-2xl font-extrabold tracking-tight text-slate-950 sm:text-3xl">
                Simple. Practical. Scalable.
              </h2>

              <p className="mt-4 text-sm leading-7 text-slate-500">
                We keep our process straightforward: understand the
                requirement, plan the right solution, build it properly and
                continue supporting the business after launch.
              </p>

              <div className="mt-6 flex items-center gap-3 text-sm font-bold text-slate-700">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                  <FaCheck />
                </span>
                Built for long-term growth
              </div>
            </div>
          </div>
        </section>

        {/* =====================================================
            INDUSTRIES
        ====================================================== */}

        <section className="mt-24" data-aos="fade-up">
          <div className="relative overflow-hidden rounded-[2rem] border border-slate-200 bg-white p-7 shadow-sm sm:p-10">
            <div className="pointer-events-none absolute left-1/2 top-0 h-56 w-[500px] -translate-x-1/2 rounded-full bg-blue-100/60 blur-[100px]" />

            <div className="relative text-center">
              <span className="text-[10px] font-extrabold tracking-[0.22em] text-blue-600">
                INDUSTRIES WE SERVE
              </span>

              <h2 className="mt-4 text-2xl font-extrabold tracking-tight text-slate-950 sm:text-3xl">
                Solutions Across Multiple Industries
              </h2>

              <p className="mx-auto mt-3 max-w-2xl text-sm leading-7 text-slate-500">
                We work with businesses from different industries and adapt
                our solutions to their specific requirements.
              </p>
            </div>

            <div className="relative mt-8 flex flex-wrap justify-center gap-3">
              {industries.map((industry) => (
                <span
                  key={industry}
                  className="rounded-full border border-slate-200 bg-slate-50 px-4 py-2.5 text-xs font-bold text-slate-500 transition duration-300 hover:-translate-y-1 hover:border-blue-200 hover:bg-blue-50 hover:text-blue-600 hover:shadow-sm"
                >
                  {industry}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* =====================================================
            WHY US
        ====================================================== */}

        <section className="mt-24" data-aos="fade-up">
          <div className="mx-auto max-w-2xl text-center">
            <span className="text-[10px] font-extrabold tracking-[0.22em] text-blue-600">
              WHY PRIMEAPPCONNECT
            </span>

            <h2 className="mt-4 text-3xl font-extrabold tracking-[-0.035em] text-slate-950 sm:text-4xl">
              Built Around
              <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                {" "}
                Your Business
              </span>
            </h2>

            <p className="mx-auto mt-4 max-w-xl text-sm leading-7 text-slate-500">
              We focus on building technology that is useful today and ready
              to support your business tomorrow.
            </p>
          </div>

          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {strengths.map((item, index) => (
              <div
                key={item.title}
                data-aos="fade-up"
                data-aos-delay={index * 80}
                className="group rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-all duration-500 hover:-translate-y-2 hover:border-blue-200 hover:shadow-[0_20px_50px_rgba(37,99,235,.10)]"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50 text-blue-600 transition duration-300 group-hover:rotate-3 group-hover:scale-110 group-hover:bg-blue-600 group-hover:text-white">
                  {item.icon}
                </div>

                <h3 className="mt-5 text-lg font-extrabold tracking-tight text-slate-900">
                  {item.title}
                </h3>

                <p className="mt-2 text-sm leading-7 text-slate-500">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* =====================================================
            FINAL CTA
        ====================================================== */}

        <section
          className="relative mt-24 overflow-hidden rounded-[2rem] bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-700 px-6 py-14 text-center shadow-[0_25px_70px_rgba(37,99,235,.22)] sm:px-10 sm:py-16"
          data-aos="zoom-in"
        >
          <div className="absolute -left-24 -top-24 h-64 w-64 animate-pulse rounded-full bg-white/10 blur-3xl" />

          <div className="absolute -bottom-24 -right-24 h-64 w-64 rounded-full bg-cyan-300/20 blur-3xl" />

          <FaStar className="absolute left-[12%] top-[25%] animate-pulse text-sm text-white/25" />

          <FaStar className="absolute right-[15%] top-[20%] animate-bounce text-xs text-white/20" />

          <div className="relative">
            <span className="text-[10px] font-extrabold tracking-[0.22em] text-blue-100">
              LET'S BUILD TOGETHER
            </span>

            <h2 className="mt-4 text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
              Have an Idea for Your Business?
            </h2>

            <p className="mx-auto mt-4 max-w-xl text-sm leading-7 text-blue-100">
              Tell us about your business and requirements. We'll help you
              choose the right digital solution.
            </p>

            <Link
              to="/contact"
              className="group mt-8 inline-flex items-center gap-2 rounded-xl bg-white px-7 py-3.5 text-sm font-extrabold text-blue-600 shadow-lg transition duration-300 hover:-translate-y-1 hover:bg-blue-50 hover:shadow-xl"
            >
              Start Your Project

              <FaArrowRight className="text-xs transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </section>
      </div>
    </main>
  );
}